/**
 * Admin Hooks (React Query)
 */

import { useQuery } from "@tanstack/react-query";
import { adminApi } from "@/api/admin";
import { useCampusFilter } from "@/hooks/useCampusFilter";
import { LIST_QUERY_OPTIONS } from "@/hooks/queryOptions";
import type {
  AuditLogParams,
  AnalyticsParams,
  DiscountAnalysisParams,
  AttendanceReportParams,
  HifdhProgressReportParams,
  FailedPaymentsReportParams,
} from "@/types/admin";

/**
 * Get dashboard metrics
 */
export const useDashboardMetrics = () => {
  const campusId = useCampusFilter();

  return useQuery({
    queryKey: ["admin", "metrics", campusId],
    queryFn: async () => {
      const response = await adminApi.getMetrics({
        campus_id: campusId || undefined,
      });
      return response.data;
    },
    staleTime: 1 * 60 * 1000, // 1 minute
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
  });
};

/**
 * Get analytics data for charts and dashboards
 */
export const useAnalytics = (params: AnalyticsParams) => {
  const campusId = useCampusFilter();
  // Stabilize campusId to avoid query key changes between undefined and null
  const stableCampusId = campusId || undefined;

  // Ensure we have valid date params before making the request
  const hasValidParams = !!(params.start_date && params.end_date);

  return useQuery({
    queryKey: ["admin", "analytics", params.start_date, params.end_date, stableCampusId],
    queryFn: async () => {
      const response = await adminApi.getAnalytics({
        ...params,
        campus_id: stableCampusId,
      });
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: hasValidParams,
  });
};

/**
 * Get audit log with filters
 */
export const useAuditLog = (params?: AuditLogParams) => {
  return useQuery({
    // Keep the current page on screen while the next one (or a new filter)
    // loads, instead of flashing a spinner.
    ...LIST_QUERY_OPTIONS,
    queryKey: ["admin", "audit-log", params],
    queryFn: async () => {
      const response = await adminApi.getAuditLog(params);
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Get discount analysis report data
 */
export const useDiscountAnalysis = (params: DiscountAnalysisParams) => {
  const campusId = useCampusFilter();
  const stableCampusId = campusId || undefined;

  const hasValidParams = !!(params.start_date && params.end_date);

  return useQuery({
    queryKey: ["admin", "discount-analysis", params.start_date, params.end_date, stableCampusId],
    queryFn: async () => {
      const response = await adminApi.getDiscountAnalysis({
        ...params,
        campus_id: stableCampusId,
      });
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: hasValidParams,
  });
};

/**
 * Get attendance report data
 */
export const useAttendanceReport = (params: AttendanceReportParams) => {
  const campusId = useCampusFilter();
  const stableCampusId = campusId || undefined;

  const hasValidParams = !!(params.start_date && params.end_date);

  return useQuery({
    queryKey: ["admin", "attendance-report", params.start_date, params.end_date, stableCampusId, params.class_id],
    queryFn: async () => {
      const response = await adminApi.getAttendanceReport({
        ...params,
        campus_id: stableCampusId,
      });
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: hasValidParams,
  });
};

/**
 * Get Hifdh progress report data
 */
export const useHifdhProgressReport = (params: HifdhProgressReportParams) => {
  const campusId = useCampusFilter();
  const stableCampusId = campusId || undefined;

  const hasValidParams = !!params.month;

  return useQuery({
    queryKey: ["admin", "hifdh-progress-report", params.month, stableCampusId, params.class_id],
    queryFn: async () => {
      const response = await adminApi.getHifdhProgressReport({
        ...params,
        campus_id: stableCampusId,
      });
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: hasValidParams,
  });
};

/**
 * Get failed payments report data
 */
export const useFailedPaymentsReport = (params: FailedPaymentsReportParams) => {
  const campusId = useCampusFilter();
  const stableCampusId = campusId || undefined;

  const hasValidParams = !!(params.start_date && params.end_date);

  return useQuery({
    queryKey: ["admin", "failed-payments-report", params.start_date, params.end_date, stableCampusId],
    queryFn: async () => {
      const response = await adminApi.getFailedPaymentsReport({
        ...params,
        campus_id: stableCampusId,
      });
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: hasValidParams,
  });
};

/**
 * Get autopay enrollment report data
 */
export const useAutopayEnrollmentReport = () => {
  const campusId = useCampusFilter();
  const stableCampusId = campusId || undefined;

  return useQuery({
    queryKey: ["admin", "autopay-enrollment-report", stableCampusId],
    queryFn: async () => {
      const response = await adminApi.getAutopayEnrollmentReport({
        campus_id: stableCampusId,
      });
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};
